import { QueryResult, useApolloClient } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { Bar, Position, Size } from '@busybusy/webapp-react-ui';
import { EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import classNames from 'classnames';
import LazyLoadPicker from 'components/foundation/pickers/LazyLoadPicker/LazyLoadPicker';
import { useDebounce, useOrganization } from 'hooks';
import _ from 'lodash';
import * as React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import SearchType from 'types/enum/SearchType';
import IEquipment from 'types/Equipment';
import { stringUtils } from 'utils';
import { getEquipmentDisplay } from 'utils/stringUtils';
import { logError } from 'utils/testUtils';

export interface IEquipmentPickerProps {
  value: string | null;
  onSelect: (equipmentId: string | null) => void;
  minWidth?: string;
  placeholder?: string;
  error?: boolean;
  position?: Position;
  dropDownButtonRender?: ReactNode; // Drop down button on the picker
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode; // Close button render on picker
  className?: string | object;
  filteredIds?: string[];
}

const EquipmentPicker = (props: IEquipmentPickerProps) => {
  const {
    value,
    onSelect,
    minWidth,
    error,
    placeholder,
    position,
    closeButtonRender,
    dropDownButtonRender,
    className,
    filteredIds,
  } = props;

  const [data, setData] = useState<IEquipment[]>([]);
  const [loadedAll, setLoadedAll] = useState(false);
  const [apolloError, setApolloError] = useState(false);
  const organization = useOrganization();
  const [searchValue, setSearchValue] = useState('');
  const searchDebounce = useDebounce(searchValue, 250);
  const client = useApolloClient();

  useEffect(() => {
    setData([]);
    setLoadedAll(false);
  }, [searchDebounce]);

  function handleDidLoad(models: IEquipment[], err: boolean, allLoaded: boolean) {
    if (!err) {
      setLoadedAll(allLoaded);
      setData(models);
    }

    setApolloError(err);
  }

  function onClose() {
    setSearchValue('');
    setLoadedAll(false);
    setData([]);
  }

  function handleSelect(model: IEquipment | null) {
    onSelect(model ? model.id : null);
  }

  async function getEquipment(after?: string): Promise<IEquipment[]> {
    const result = await client
      .query<{ equipment: IEquipment[] }>({
        query: EQUIPMENT_QUERY,
        variables: {
          after,
          first: 10,
          filter: {
            deletedOn: { isNull: true },
            search: searchDebounce
              ? {
                  type: SearchType.CONTAINS,
                  fields: ['equipmentName', 'modelName', 'makeName'],
                  value: searchDebounce,
                }
              : undefined,
            id: filteredIds ? { doesNotContain: filteredIds } : undefined,
          },
        },
      })
      .catch(logError);

    return result.data.equipment;
  }

  function renderValueTemplate(id: string | null) {
    if (id) {
      const equipment = _.find(data, (model) => model.id === id);
      if (equipment) {
        return <div className="ellipsis">{equipment ? getEquipmentDisplay(equipment) : 'None'}</div>;
      } else {
        return (
          <Query query={EQUIPMENT_QUERY} variables={{ filter: { id: { equal: id } } }}>
            {({ data: equipmentData, error }: QueryResult<{ equipment: IEquipment[] }>) => {
              if (error) {
                logError(error);
              }

              return (
                <>
                  {equipmentData && equipmentData.equipment.length ? (
                    getEquipmentDisplay(equipmentData.equipment[0])
                  ) : (
                    <></>
                  )}
                </>
              );
            }}
          </Query>
        );
      }
    }

    return <></>;
  }

  function renderRow(equipment: IEquipment) {
    return (
      <Bar size={Size.SMALL} className="px-3">
        <div className="ellipsis">
          {searchValue
            ? stringUtils.highlight(getEquipmentDisplay(equipment), searchValue)
            : getEquipmentDisplay(equipment)}
        </div>
      </Bar>
    );
  }

  function onSearch(val: string) {
    setSearchValue(val);
  }

  const classes = classNames('equipment-picker', className);

  return organization.trackEquipment ? (
    <LazyLoadPicker
      value={value}
      getData={getEquipment}
      dataError={apolloError}
      didLoad={handleDidLoad}
      error={error}
      data={data}
      loadedAll={loadedAll}
      onSelect={handleSelect}
      onClose={onClose}
      onSearch={onSearch}
      searchValue={searchValue}
      valueTemplate={renderValueTemplate}
      renderRow={renderRow}
      minWidth={minWidth}
      position={position}
      placeholder={placeholder}
      className={classes}
      closeButtonRender={closeButtonRender}
      dropDownButtonRender={dropDownButtonRender}
    />
  ) : null;
};

EquipmentPicker.defaultProps = {
  minWidth: '350px',
};

export default EquipmentPicker;
